import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, LinearProgress } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import { useLazyQuery, useQuery } from '@apollo/client';
import { CHECKOUT_STATUS, GET_USER_BILLING } from '../../graphql/billingQueries';
import { formatPlanIdForDisplay } from './billingModel';
import PaymentResultPanel from './PaymentResultPanel';

const MAX_POLL_ATTEMPTS = 20;
const POLL_INTERVAL_MS = 2000;

/**
 * 'confirmed' - the account shows this checkout's subscription.
 * 'pending'   - we couldn't see that yet (the webhook hasn't landed, or the
 *               API couldn't be asked). NOT a success claim.
 * 'failed'    - the payment didn't happen (a declined Moneris payment, or a
 *               Stripe checkout that expired unpaid).
 */
type PaymentOutcome = 'confirmed' | 'pending' | 'failed';

interface PaymentState {
  /** Still waiting for an answer (polling, or the Moneris delay). */
  loading: boolean;
  outcome: PaymentOutcome;
  /** The plan the confirmed subscription is on, when we know it. */
  planId: string | null;
}

const SUPPORT_MAILTO = 'mailto:info@artbits.solutions?subject=Payment%20confirmation';

/**
 * Where Stripe Checkout (session_id) and the legacy Moneris checkout
 * (response_code) return. The webhook is what activates a subscription, so
 * for Stripe this page asks the API about this very checkout and says
 * "confirmed" once the account shows its subscription, which also works when
 * the webhook landed before the page loaded. Until then it says it is still
 * confirming. Opened directly, with neither parameter, there is nothing to
 * confirm: it goes to Plan and billing.
 */
const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const stripeSessionId = params.get('session_id');
  const monerisResponseCode = params.get('response_code');
  const hasCheckout = Boolean(stripeSessionId) || monerisResponseCode !== null;

  const [state, setState] = useState<PaymentState>({ loading: true, outcome: 'pending', planId: null });

  // Refs to avoid stale closures in the polling effect.
  const attemptsRef = useRef(0);
  const pollingCompleteRef = useRef(false);

  // Refetched once confirmed, so Plan and billing shows the new plan at once.
  const { refetch: refetchBilling } = useQuery(GET_USER_BILLING, {
    fetchPolicy: 'network-only',
    skip: !hasCheckout,
  });
  const [checkCheckout] = useLazyQuery(CHECKOUT_STATUS, { fetchPolicy: 'network-only' });

  useEffect(() => {
    // Stripe redirect: ask right away (the webhook has usually landed by
    // now), then every 2 seconds, each check waiting for the one before.
    if (stripeSessionId) {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      let cancelled = false;

      const finish = (next: PaymentState) => {
        pollingCompleteRef.current = true;
        if (timeoutId) clearTimeout(timeoutId);
        setState(next);
      };

      const check = async () => {
        if (cancelled || pollingCompleteRef.current) return;
        attemptsRef.current += 1;

        try {
          const { data, error } = await checkCheckout({ variables: { sessionId: stripeSessionId } });
          if (error) throw error;
          if (cancelled) return;
          const status = data?.checkoutStatus;
          if (status?.state === 'applied') {
            refetchBilling().catch(() => undefined);
            finish({ loading: false, outcome: 'confirmed', planId: status.planId ?? null });
            return;
          }
          if (status?.state === 'expired') {
            finish({ loading: false, outcome: 'failed', planId: null });
            return;
          }
          // paid (webhook not landed yet), open or unknown: ask again.
        } catch (err) {
          console.error('Payment status check failed:', err);
          // Keep asking on transient errors until the limit.
        }
        if (cancelled) return;

        // Failed checks count too, so a page that can't reach the API stops
        // after the same number of tries instead of asking forever.
        if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          // We genuinely do not know yet - say so rather than claiming success.
          finish({ loading: false, outcome: 'pending', planId: null });
          return;
        }
        timeoutId = setTimeout(() => void check(), POLL_INTERVAL_MS);
      };

      void check();

      return () => {
        cancelled = true;
        if (timeoutId) clearTimeout(timeoutId);
      };
    }

    // Moneris redirect (legacy path).
    if (monerisResponseCode !== null) {
      const timeoutId = setTimeout(() => {
        setState({
          loading: false,
          outcome: parseInt(monerisResponseCode, 10) < 50 ? 'confirmed' : 'failed',
          planId: null,
        });
      }, 2000);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [stripeSessionId, monerisResponseCode, refetchBilling, checkCheckout]);

  if (!hasCheckout) {
    return <Navigate to="/billing" replace />;
  }

  const toBilling = { label: 'Go to plan and billing', onClick: () => navigate('/billing') };
  const contactSupport = {
    label: 'Contact support',
    onClick: () => {
      window.location.href = SUPPORT_MAILTO;
    },
  };

  if (state.loading) {
    return (
      <PaymentResultPanel
        tone="active"
        icon={<HourglassEmptyOutlinedIcon />}
        title="Confirming your payment"
        description="This usually takes a few seconds. You can leave this page; your plan updates on its own."
        secondaryAction={toBilling}
      >
        <Box sx={{ maxWidth: 320, mx: 'auto', mt: 3 }}>
          {/* Teal, the tile's "in progress" tone; copper stays for actions. */}
          <LinearProgress color="secondary" aria-label="Confirming your payment" sx={{ height: 4, borderRadius: 2 }} />
        </Box>
      </PaymentResultPanel>
    );
  }

  if (state.outcome === 'confirmed') {
    const planName = state.planId ? formatPlanIdForDisplay(state.planId) : null;
    return (
      <PaymentResultPanel
        tone="done"
        icon={<CheckCircleOutlinedIcon />}
        title="Payment confirmed"
        description={
          stripeSessionId
            ? `Your ${planName ? `${planName} ` : ''}subscription is active and your card is on file.`
            : 'Your payment went through and your plan is updated.'
        }
        primaryAction={toBilling}
        secondaryAction={{ label: 'Go to dashboard', onClick: () => navigate('/dashboard') }}
      />
    );
  }

  if (state.outcome === 'pending') {
    return (
      <PaymentResultPanel
        tone="neutral"
        icon={<HourglassEmptyOutlinedIcon />}
        title="Still confirming your payment"
        description="Checkout is complete, but the payment provider hasn't confirmed it yet. This usually takes under a minute, and your plan updates on its own. If it still looks wrong in a few minutes, contact support and we'll sort it out."
        primaryAction={toBilling}
        secondaryAction={contactSupport}
      />
    );
  }

  return (
    <PaymentResultPanel
      tone="problem"
      icon={<ErrorOutlineOutlinedIcon />}
      title="Payment didn't go through"
      description="Your payment wasn't completed. Try again, or contact support if this keeps happening."
      primaryAction={{ label: 'Choose a plan', onClick: () => navigate('/billing', { state: { openPlanPicker: true } }) }}
      secondaryAction={contactSupport}
    />
  );
};

export default PaymentSuccess;
